import React, { useState, useEffect, useCallback } from "react";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";

import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

import ChatIcon from "@material-ui/icons/Chat";
import PeopleIcon from "@material-ui/icons/People";
import MessageIcon from "@material-ui/icons/Message";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import SendIcon from "@material-ui/icons/Send";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import RefreshIcon from "@material-ui/icons/Refresh";
import PersonIcon from "@material-ui/icons/Person";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  kpiCard: {
    height: "100%",
    borderLeft: "4px solid",
  },
  kpiContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px !important",
  },
  kpiValue: {
    fontSize: "1.8rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  kpiIcon: {
    fontSize: 40,
    opacity: 0.25,
  },
  chartPaper: {
    padding: theme.spacing(2),
    height: 350,
  },
  tablePaper: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.95rem",
  },
  filterBar: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  heatmapContainer: {
    padding: theme.spacing(2),
  },
  heatmapGrid: {
    display: "grid",
    gridTemplateColumns: "50px repeat(24, 1fr)",
    gap: 2,
  },
  heatmapCell: {
    aspectRatio: "1",
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.6rem",
    color: "#fff",
    minHeight: 24,
  },
  heatmapLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: 6,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  heatmapHourLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
  },
  responseChip: {
    fontWeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
}));

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

const getHeatColor = (count, max) => {
  if (count === 0) return "#f0f0f0";
  const intensity = Math.min(count / Math.max(max, 1), 1);
  if (intensity < 0.25) return "#c8e6c9";
  if (intensity < 0.5) return "#66bb6a";
  if (intensity < 0.75) return "#2e7d32";
  return "#1b5e20";
};

const formatMinutes = (min) => {
  if (min === null || min === undefined) return "N/A";
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}h ${m}m`;
};

const Dashboard = () => {
  const classes = useStyles();
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: stats } = await api.get("/reports/dashboard-stats", {
        params: { dateFrom, dateTo },
      });
      setData(stats);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, []);

  const setQuickDate = (days) => {
    setDateFrom(format(subDays(new Date(), days), "yyyy-MM-dd"));
    setDateTo(format(new Date(), "yyyy-MM-dd"));
  };

  // Build heatmap matrix
  const buildHeatmap = () => {
    if (!data) return [];
    const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
    let max = 0;
    data.heatmap.forEach((h) => {
      const dayIdx = h.dayOfWeek - 1; // MySQL: 1=Sunday
      matrix[dayIdx][h.hour] = h.count;
      if (h.count > max) max = h.count;
    });
    return { matrix, max };
  };

  const heatmapInfo = data ? buildHeatmap() : { matrix: [], max: 0 };

  // Avg response across all agents
  const avgResponse = data?.agentPerformance?.length
    ? data.agentPerformance.reduce((sum, a) => sum + (a.avgResponseMinutes || 0), 0) /
      data.agentPerformance.filter((a) => a.avgResponseMinutes !== null).length
    : null;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Panel</Title>
        <MainHeaderButtonsWrapper>
        <TextField
          label="Desde"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          style={{ width: 160 }}
        />
        <TextField
          label="Hasta"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          style={{ width: 160 }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={fetchData}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={loading}
        >
          Actualizar
        </Button>
        <ButtonGroup size="small" variant="text" color="primary">
          <Button onClick={() => setQuickDate(7)}>7d</Button>
          <Button onClick={() => setQuickDate(15)}>15d</Button>
          <Button onClick={() => setQuickDate(30)}>30d</Button>
          <Button onClick={() => setQuickDate(90)}>90d</Button>
        </ButtonGroup>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">

      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPIs Row 1 */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            {[
              { label: "Total Chats", value: data.kpis.totalTickets, color: "#1976d2", icon: ChatIcon },
              { label: "Contactos", value: data.kpis.totalContacts, color: "#4CAF50", icon: PeopleIcon },
              { label: "Abiertos", value: data.kpis.openTickets, color: "#FF9800", icon: HourglassEmptyIcon },
              { label: "Cerrados", value: data.kpis.closedTickets, color: "#4CAF50", icon: CheckCircleIcon },
              { label: "Mensajes", value: data.kpis.totalMessages, color: "#9C27B0", icon: MessageIcon },
              { label: "Enviados", value: data.kpis.sentMessages, color: "#2196F3", icon: SendIcon },
              { label: "Recibidos", value: data.kpis.receivedMessages, color: "#FF5722", icon: CallReceivedIcon },
              { label: "Resp. Promedio", value: formatMinutes(avgResponse), color: "#607D8B", icon: AccessTimeIcon },
            ].map((kpi, i) => (
              <Grid item xs={6} sm={3} md={3} lg={true} key={i}>
                <Card className={classes.kpiCard} elevation={2} style={{ borderLeftColor: kpi.color }}>
                  <CardContent className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: kpi.color }}>
                        {kpi.value}
                      </Typography>
                      <Typography className={classes.kpiLabel}>{kpi.label}</Typography>
                    </div>
                    <kpi.icon className={classes.kpiIcon} style={{ color: kpi.color }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Charts Row 1: Tendencia + Heatmap */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} md={8}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <TrendingUpIcon color="primary" fontSize="small" /> Tendencia diaria
                </Typography>
                <ResponsiveContainer width="100%" height={290}>
                  <AreaChart data={data.dailyTrend}>
                    <defs>
                      <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9C27B0" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#9C27B0" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd MMM", { locale: es }); }
                        catch { return d; }
                      }}
                      fontSize={11}
                    />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <RechartsTooltip
                      labelFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd 'de' MMMM yyyy", { locale: es }); }
                        catch { return d; }
                      }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="tickets" name="Chats" stroke="#1976d2" fillOpacity={1} fill="url(#colorTickets)" strokeWidth={2} />
                    <Area type="monotone" dataKey="messages" name="Mensajes" stroke="#9C27B0" fillOpacity={1} fill="url(#colorMessages)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <AccessTimeIcon color="primary" fontSize="small" /> Chats por hora
                </Typography>
                <ResponsiveContainer width="100%" height={290}>
                  <BarChart
                    data={Array.from({ length: 24 }, (_, i) => ({
                      hour: `${i}h`,
                      count: data.hourlyDistribution.find((h) => h.hour === i)?.count || 0,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" fontSize={10} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <RechartsTooltip />
                    <Bar dataKey="count" name="Chats" fill="#1976d2" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Heatmap */}
          <Paper className={classes.heatmapContainer} elevation={2} style={{ marginBottom: 16 }}>
            <Typography className={classes.sectionTitle}>
              <ChatIcon color="primary" fontSize="small" /> Mapa de calor - Chats por dia y hora
            </Typography>
            <div className={classes.heatmapGrid}>
              {/* Header row */}
              <div />
              {Array.from({ length: 24 }, (_, i) => (
                <div key={`h-${i}`} className={classes.heatmapHourLabel}>
                  {i % 2 === 0 ? `${i}` : ""}
                </div>
              ))}
              {/* Data rows */}
              {heatmapInfo.matrix.map((row, dayIdx) => (
                <React.Fragment key={dayIdx}>
                  <div className={classes.heatmapLabel}>{DAY_NAMES[dayIdx]}</div>
                  {row.map((count, hour) => (
                    <Tooltip key={hour} title={`${DAY_NAMES[dayIdx]} ${hour}:00 - ${count} chats`} arrow>
                      <div
                        className={classes.heatmapCell}
                        style={{ backgroundColor: getHeatColor(count, heatmapInfo.max) }}
                      >
                        {count > 0 ? count : ""}
                      </div>
                    </Tooltip>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </Paper>

          {/* Agent Performance + Top Contacts */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper className={classes.tablePaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <PersonIcon color="primary" fontSize="small" /> Rendimiento por agente
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Agente</strong></TableCell>
                        <TableCell align="center"><strong>Chats</strong></TableCell>
                        <TableCell align="center"><strong>Cerrados</strong></TableCell>
                        <TableCell align="center"><strong>Msgs Enviados</strong></TableCell>
                        <TableCell align="center"><strong>Resp. Promedio</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.agentPerformance.map((agent) => (
                        <TableRow key={agent.userId} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Avatar style={{ width: 28, height: 28, fontSize: 12, backgroundColor: "#1976d2" }}>
                                {agent.userName.charAt(0)}
                              </Avatar>
                              {agent.userName}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{agent.totalTickets}</TableCell>
                          <TableCell align="center">{agent.closedTickets}</TableCell>
                          <TableCell align="center">{agent.messagesSent}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={formatMinutes(agent.avgResponseMinutes)}
                              size="small"
                              className={classes.responseChip}
                              style={{
                                backgroundColor:
                                  agent.avgResponseMinutes === null
                                    ? "#e0e0e0"
                                    : agent.avgResponseMinutes < 30
                                    ? "#c8e6c9"
                                    : agent.avgResponseMinutes < 60
                                    ? "#fff3e0"
                                    : "#ffcdd2",
                                color:
                                  agent.avgResponseMinutes === null
                                    ? "#999"
                                    : agent.avgResponseMinutes < 30
                                    ? "#2e7d32"
                                    : agent.avgResponseMinutes < 60
                                    ? "#e65100"
                                    : "#c62828",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.agentPerformance.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">Sin datos</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper className={classes.tablePaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <PeopleIcon color="primary" fontSize="small" /> Top 10 contactos
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Contacto</strong></TableCell>
                        <TableCell align="center"><strong>Chats</strong></TableCell>
                        <TableCell align="center"><strong>Msgs</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.topContacts.map((contact, idx) => (
                        <TableRow key={contact.contactId} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Avatar
                                style={{
                                  width: 28,
                                  height: 28,
                                  fontSize: 11,
                                  backgroundColor: idx < 3 ? "#FF9800" : "#bdbdbd",
                                }}
                              >
                                {idx + 1}
                              </Avatar>
                              <div>
                                <Typography variant="body2" style={{ fontWeight: 500, lineHeight: 1.2 }}>
                                  {contact.contactName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {contact.contactNumber}
                                </Typography>
                              </div>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{contact.totalTickets}</TableCell>
                          <TableCell align="center">{contact.totalMessages}</TableCell>
                        </TableRow>
                      ))}
                      {data.topContacts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">Sin datos</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      </Paper>
    </MainContainer>
  );
};

export default Dashboard;
